import { useCallback, useEffect, useMemo, useState } from 'react';
import { DECKS } from '../../config/decks';
import { CardContent, figureSrc } from '../../components/CardContent';
import { chapterBoost, chapterPerformance, dailyTarget, examMultiplier, newCardsStudiedToday } from '../../domain/analytics/analytics';
import { buildQueue, formatDue, previewIntervals, review } from '../../domain/srs/srs';
import * as storage from '../../services/storage/storage';
import type { AppData, Card, DeckId, ReviewRating } from '../../types';

interface Props {
  activeDeckId: DeckId;
  cards: Record<DeckId, Card[]>;
  data: AppData;
  onDeckChange: (deckId: DeckId) => void;
  onDataChanged: () => void;
  onActiveCardChange: (deckId: DeckId, card: Card | null) => void;
}

export function ReviewView({ activeDeckId, cards, data, onDeckChange, onDataChanged, onActiveCardChange }: Props) {
  const [queue, setQueue] = useState<Card[]>([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState(Date.now());
  // Anzahl zusätzlich freigegebener neuer Karten über das Tagesziel hinaus,
  // ausgelöst durch den "Manuell weiterlernen"-Button.
  const [extraNew, setExtraNew] = useState<Record<DeckId, number>>({} as Record<DeckId, number>);

  const deck = DECKS.find((item) => item.id === activeDeckId)!;
  const deckCards = cards[activeDeckId] ?? [];
  const deckState = data.decks[activeDeckId] ?? { cards: {}, examDate: null };
  const current = queue[index] ?? null;
  const extraForDeck = extraNew[activeDeckId] ?? 0;

  const counts = useMemo(() => {
    const perf = chapterPerformance(deckCards, deckState.cards, data.history, activeDeckId);
    const boost = chapterBoost(perf, data.settings);
    const target = dailyTarget(deckCards, deckState.cards, deckState.examDate, data.settings);
    const dailyNewLimit = data.settings.maxNewPerDay || target.newPerDay;
    const introducedToday = newCardsStudiedToday(data.history, activeDeckId);
    const plannedRemaining = Math.max(0, dailyNewLimit - introducedToday);
    const remainingNewLimit = plannedRemaining + extraForDeck;
    const result = buildQueue(deckCards, deckState.cards, { newLimit: remainingNewLimit, weakChapterBoost: boost });
    return { ...result, plannedRemaining };
  }, [activeDeckId, data, deckCards, deckState.cards, deckState.examDate, extraForDeck]);

  const addExtraNew = useCallback((amount: number) => {
    setExtraNew((prev) => ({ ...prev, [activeDeckId]: (prev[activeDeckId] ?? 0) + amount }));
  }, [activeDeckId]);

  const newCardsAvailable = useMemo(
    () => deckCards.filter((card) => {
      const state = deckState.cards[card.id];
      return !state?.suspended && (!state || state.state === 'new');
    }).length,
    [deckCards, deckState.cards],
  );
  const dailyPlanMet = counts.plannedRemaining === 0 && newCardsAvailable > 0;

  useEffect(() => {
    setQueue(counts.queue);
    setIndex(0);
    setFlipped(false);
    setStartedAt(Date.now());
  }, [counts.queue]);

  useEffect(() => {
    onActiveCardChange(activeDeckId, current);
  }, [activeDeckId, current, onActiveCardChange]);

  const rate = useCallback((q: ReviewRating) => {
    if (!current) return;
    const previous = { ...storage.getCardState(activeDeckId, current.id) };
    const next = review(previous, q, {
      retentionTarget: data.settings.targetRetention,
      examMultiplier: examMultiplier(deckState.examDate, data.settings),
    });
    storage.setCardState(activeDeckId, current.id, next);
    storage.logReview({
      ts: Date.now(),
      deckId: activeDeckId,
      cardId: current.id,
      q,
      prevState: previous.state,
      prevInterval: previous.interval,
      newInterval: next.interval,
      ms: Date.now() - startedAt,
    });
    storage.bumpStreak();
    onDataChanged();
    setFlipped(false);
    setStartedAt(Date.now());
    setIndex((value) => value + 1);
  }, [activeDeckId, current, data.settings, deckState.examDate, onDataChanged, startedAt]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        setFlipped((value) => !value);
      }
      if (!flipped) return;
      if (event.key === '1') rate(0);
      if (event.key === '2') rate(3);
      if (event.key === '3') rate(4);
      if (event.key === '4') rate(5);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [flipped, rate]);

  const state = current ? storage.getCardState(activeDeckId, current.id) : null;
  const previews = state ? previewIntervals(state, { retentionTarget: data.settings.targetRetention, examMultiplier: examMultiplier(deckState.examDate, data.settings) }) : null;
  const progressPct = queue.length ? Math.round((index / queue.length) * 100) : 100;
  const figure = current ? figureSrc(current.figure) : null;

  return (
    <section className="view active">
      <header className="page-head">
        <div>
          <h1>Lernen</h1>
          <p className="muted">{deck.icon} {deck.name} · {Math.min(index + 1, queue.length)} / {queue.length || 0}</p>
        </div>
        <div className="head-actions">
          <label className="select-wrap">
            <span className="sr-only">Fach wählen</span>
            <select className="select" value={activeDeckId} onChange={(event) => onDeckChange(event.target.value as DeckId)}>
              {DECKS.map((item) => <option key={item.id} value={item.id}>{item.icon}  {item.name}</option>)}
            </select>
          </label>
          <button className="btn btn-secondary" onClick={() => setHidden((value) => !value)}>{hidden ? 'Karten anzeigen' : 'Karten verbergen'}</button>
          {dailyPlanMet && newCardsAvailable - extraForDeck > 0 && (
            <button
              className="btn btn-secondary"
              title="Fügt zusätzliche neue Karten über das Tagesziel hinaus zur Sitzung hinzu"
              onClick={() => addExtraNew(Math.min(5, newCardsAvailable - extraForDeck))}
            >
              ➕ Manuell weiterlernen
            </button>
          )}
        </div>
      </header>

      <div className="panel review-panel">
        <div className="review-progress" aria-label="Lernfortschritt">
          <div className="rp-labels">
            <span><span className="dot-new" /> Neu: {counts.counts.new}</span>
            <span><span className="dot-learn" /> Lernen: {counts.counts.learning}</span>
            <span><span className="dot-due" /> Fällig: {counts.counts.due}</span>
            <strong>{Math.min(index, queue.length)} / {queue.length || 0}</strong>
          </div>
          <div className="rp-bar"><span className="rp-fill" style={{ width: `${progressPct}%` }} /></div>
        </div>

        {!current ? (
          <div className="empty-state">
            <h2>Session fertig 🎉</h2>
            <p className="muted">
              {dailyPlanMet
                ? 'Tagesziel erreicht. Es sind aktuell keine fälligen Karten mehr für dieses Deck offen.'
                : 'Für dieses Deck sind aktuell keine Karten fällig.'}
            </p>
            {newCardsAvailable - extraForDeck > 0 && (
              <button className="btn btn-primary" onClick={() => addExtraNew(Math.min(10, newCardsAvailable - extraForDeck))}>
                ➕ Manuell weiterlernen
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={`card-stage ${flipped ? 'flipped' : ''}`}>
              <button className="card-inner" onClick={() => setFlipped((value) => !value)} aria-label={flipped ? 'Frage zeigen' : 'Antwort zeigen'}>
                <div className="card-face card-front">
                  <div className="face-label">Karte {current.number} · {current.chapter}</div>
                  <div className="face-content">{hidden ? <p className="muted">Karteninhalt verborgen</p> : <CardContent text={current.front} />}</div>
                  <div className="face-foot muted">Klicke oder drücke <kbd>Space</kbd> zum Umdrehen</div>
                </div>
                <div className="card-face card-back">
                  <div className="face-label">Antwort · {state?.state ?? 'new'} · fällig {formatDue(state?.due ?? null)}</div>
                  <div className="face-content">{hidden ? <p className="muted">Karteninhalt verborgen</p> : <CardContent text={current.back} />}</div>
                  {figure && (
                    <span className="card-fig-hint" onClick={(event) => { event.stopPropagation(); setLightbox(figure); }} role="button" tabIndex={0}>
                      <img className="card-figure" src={figure} loading="lazy" alt={`Abbildung zu Karte ${current.number}`} />
                      Abbildung vergrößern
                    </span>
                  )}
                  {current.source && <span className="card-source">Quelle: <a href={current.source} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>{current.source}</a></span>}
                </div>
              </button>
            </div>
            <div className="review-actions">
              {!flipped ? (
                <button className="btn btn-primary btn-flip" onClick={() => setFlipped(true)}>Antwort zeigen <kbd>Space</kbd></button>
              ) : (
                <>
                  <RateButton q={0} keyHint="1" className="rate-again" label="Nochmal" due={previews?.[0]} onRate={rate} />
                  <RateButton q={3} keyHint="2" className="rate-hard" label="Schwer" due={previews?.[3]} onRate={rate} />
                  <RateButton q={4} keyHint="3" className="rate-good" label="Gut" due={previews?.[4]} onRate={rate} />
                  <RateButton q={5} keyHint="4" className="rate-easy" label="Einfach" due={previews?.[5]} onRate={rate} />
                </>
              )}
            </div>
            <div className="review-tools">
              {flipped && (
                <button className="btn-link" onClick={() => setFlipped(false)}>↶ Zurück zur Frage</button>
              )}
              <button className="btn-link" onClick={() => { setFlipped(false); setIndex((value) => Math.min(value + 1, queue.length)); }}>Überspringen →</button>
              <button className="btn-link danger" onClick={() => {
                if (!current) return;
                storage.setCardSuspended(activeDeckId, current.id, true);
                onDataChanged();
                setFlipped(false);
                setIndex((value) => value + 1);
              }}>🚫 Karte ausblenden</button>
            </div>
            <p className="review-hints muted">Tastatur: <kbd>Space</kbd>/<kbd>Enter</kbd> drehen · <kbd>1</kbd> Nochmal · <kbd>2</kbd> Schwer · <kbd>3</kbd> Gut · <kbd>4</kbd> Einfach</p>
          </>
        )}
      </div>
      {lightbox && <button className="lightbox show" onClick={() => setLightbox(null)}><img src={lightbox} alt="Vergrößerte Abbildung" /></button>}
    </section>
  );
}

function RateButton({ q, label, due, onRate, className, keyHint }: { q: ReviewRating; label: string; due?: string | null; className: string; keyHint: string; onRate: (q: ReviewRating) => void }) {
  return (
    <button className={`rate-btn ${className}`} onClick={() => onRate(q)}>
      <span className="rate-key"><kbd>{keyHint}</kbd></span>
      <strong className="rate-label">{label}</strong>
      <span className="rate-due">{formatDue(due ?? null)}</span>
    </button>
  );
}
